import { resolveBatchList } from "#components/ui/tool-approval/renderers/index.ts";
import { ToolApprovalBatchSummary } from "#components/ui/tool-approval/ToolApprovalBatchSummary.tsx";
import { ToolApprovalCard } from "#components/ui/tool-approval/ToolApprovalCard.tsx";
import { AnsweredActionCard } from "#features/conversations/components/prepared-action/PreparedActionCard.tsx";
import { useIsAssistantGenerating } from "#features/conversations/hooks/useIsAssistantGenerating.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import type { PreparedActionStatus, ToolApprovalCall } from "#types/types.ts";
import { Flex } from "@hopper-ui/components";

interface Props {
    conversationId: string;
    message: ConversationMessageModel;
    // The toolCallIds of approved calls that waited a minute with no saved result.
    unsettled: ReadonlySet<string>;
    // The approvals are the turn's only assistant content, so they take an assistant message's inset.
    isTurnBlock?: boolean;
}

// The arguments the call ran with, where the card shows them. The saved result holds the whole parameters model, defaults
// and field names included, so only the keys the model proposed are read from it. The keys the manager edited can't be told
// apart from the defaults, so the one the calendar card lets them set when the model left it out is named.
function shownArgs({ args, executedArgs }: ToolApprovalCall): Record<string, unknown> {
    return executedArgs ? { ...args, ...Object.fromEntries(Object.entries(executedArgs).filter(([key]) => Object.hasOwn(args, key) || key === "is_online_event")) } : args;
}

// Null while the call waits for a decision: the approval prompt holds it then.
function answeredStatus({ decision, outcome }: ToolApprovalCall): PreparedActionStatus | null {
    switch (decision) {
        case "allow":
            if (outcome === "succeeded") {
                return "sent";
            }

            return outcome === "failed" ? "failed" : "approved";
        case "deny":
            return "discarded";
        case "cancelled":
            return "cancelled";
        default:
            return null;
    }
}

// The approvals shown on one message: each answered call as a read-only card, then the prompt while one is pending.
export function MessageToolApprovals({ conversationId, message, unsettled, isTurnBlock = false }: Props) {
    const isGenerating = useIsAssistantGenerating(conversationId);
    // Core keeps one pending approval per conversation (Conversation.pending_tool_approval), so one prompt covers every open interrupt.
    const pendingApproval = message.toolApprovals?.find(({ status }) => status === "pending");
    const answered = (message.toolApprovals ?? []).map(({ calls, isAnsweredHere }) => ({
        isAnsweredHere,
        // A listed batch answers as one summary, so only once every call has its answer.
        summaryBatch: calls.every(call => answeredStatus(call) !== null) ? resolveBatchList(calls) : null,
        calls: calls.flatMap(call => {
            const status = answeredStatus(call);

            return status === null ? [] : [{ call, status }];
        })
    }));

    if (answered.every(({ calls }) => calls.length === 0) && !pendingApproval) {
        return null;
    }

    const approvals = (
        <>
            {answered.map(({ calls, summaryBatch, isAnsweredHere }) => (summaryBatch
                ? <ToolApprovalBatchSummary key={calls[0]!.call.toolCallId} batch={summaryBatch} calls={calls.map(({ call, status }) => ({ call: { ...call, args: shownArgs(call) }, status, isUnsettled: unsettled.has(call.toolCallId) }))} />
                : calls.map(({ call, status }) => (
                    <AnsweredActionCard
                        key={call.toolCallId}
                        call={{ ...call, args: shownArgs(call) }}
                        status={status}
                        executedAt={call.executedAt}
                        isUnsettled={unsettled.has(call.toolCallId)}
                        isSending={isGenerating && isAnsweredHere}
                    />
                ))))}
            {pendingApproval && <ToolApprovalCard conversationId={conversationId} approval={pendingApproval} />}
        </>
    );

    return isTurnBlock ? <Flex direction="column" gap="inline-md" padding="inset-md">{approvals}</Flex> : approvals;
}
